<template>
  <div class="invoice-form" :class="{ 'create-mode': isCreateMode }">
    <h4 class="form-title">
      {{ isCreateMode ? "Создание счёта" : "Редактирование счёта" }}
    </h4>

    <div class="form-grid">
      <div class="form-group">
        <base-label>Номер счёта</base-label>
        <base-input
          v-model="form.number"
          placeholder="Введите номер счёта"
        />
      </div>

      <div class="form-group">
        <base-label>Статус</base-label>
        <base-select
          v-model="form.status"
          :options="invoiceStatusOptions"
          :clearable="false"
          placeholder="Выберите статус"
        />
      </div>

      <div class="form-group">
        <base-label :required="true">Заказчик</base-label>
        <CounterpartySelect
          v-model="form.customer_id"
          placeholder="Выберите заказчика"
          :clearable="false"
          :with-button="true"
          button-label="Создать контрагента"
          @change="clearError('customer_id')"
          @button-click="openCounterpartyModal('customer')"
        />
        <span v-if="errors.customer_id" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="form-group">
        <base-label :required="true">Исполнитель</base-label>
        <CounterpartySelect
          v-model="form.contractor_id"
          placeholder="Выберите исполнителя"
          :clearable="false"
          :with-button="true"
          button-label="Создать контрагента"
          @change="clearError('contractor_id')"
          @button-click="openCounterpartyModal('contractor')"
        />
        <span v-if="errors.contractor_id" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="form-group">
        <base-label>Сумма</base-label>
        <base-input
          v-model="form.amount"
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
        />
      </div>

      <div class="form-group">
        <base-label>Валюта</base-label>
        <base-input v-model="form.currency" placeholder="RUB" />
      </div>

      <div class="form-group">
        <base-label>Дата выставления</base-label>
        <base-input v-model="form.issued_on" type="date" />
      </div>

      <div class="form-group">
        <base-label>Срок оплаты</base-label>
        <base-input v-model="form.due_on" type="date" />
      </div>

      <div class="form-group">
        <base-label>Дата оплаты</base-label>
        <base-input v-model="form.paid_on" type="date" />
      </div>
    </div>

    <div class="form-actions">
      <base-primary-button @click="save" :loading="isLoading">
        {{ isCreateMode ? "Создать" : "Сохранить" }}
      </base-primary-button>
      <base-button @click="$emit('cancel')">Отмена</base-button>
    </div>

    <AddCounterpartyModal
      v-if="isCounterpartyModalVisible"
      @counterparty-created="handleCounterpartyCreated"
      @close="isCounterpartyModalVisible = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useOrderInvoiceStore } from "@/stores/orderInvoiceStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useCounterpartyStore } from "@/stores/counterpartyStore";

import CounterpartySelect from "@/components/Counterparty/CounterpartySelect.vue";
import AddCounterpartyModal from "@/components/Counterparty/AddCounterpartyModal.vue";

export default {
  name: "OrderInvoiceForm",
  components: {
    CounterpartySelect,
    AddCounterpartyModal,
  },
  props: {
    orderId: { type: [String, Number], required: true },
    companyId: { type: [String, Number], required: true },
    initialData: { type: Object, default: null },
    isCreateMode: { type: Boolean, default: false },
  },
  emits: ["save", "cancel"],
  data() {
    return {
      form: {
        number: "",
        status: "draft",
        customer_id: null,
        contractor_id: null,
        amount: "",
        currency: "RUB",
        issued_on: "",
        due_on: "",
        paid_on: "",
      },
      errors: {
        customer_id: false,
        contractor_id: false,
      },

      isCounterpartyModalVisible: false,
      counterpartyModalTarget: null,
    };
  },
  computed: {
    ...mapState(useOrderInvoiceStore, [
      "isLoading",
      "invoiceStatusOptions",
    ]),

    notificationStore() {
      return useNotificationStore();
    },
  },
  mounted() {
    if (this.initialData) {
      this.form = {
        number: this.initialData.number || "",
        status: this.initialData.status || "draft",
        customer_id: this.initialData.customer_id || null,
        contractor_id: this.initialData.contractor_id || null,
        amount: this.initialData.amount || "",
        currency: this.initialData.currency || "RUB",
        issued_on: this.initialData.issued_on || "",
        due_on: this.initialData.due_on || "",
        paid_on: this.initialData.paid_on || "",
      };
    }
  },
  methods: {
    ...mapActions(useOrderInvoiceStore, [
      "createInvoice",
      "updateInvoice",
    ]),
    ...mapActions(useCounterpartyStore, ["fetchCounterparties"]),

    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = false;
      }
    },

    openCounterpartyModal(target) {
      this.counterpartyModalTarget = target;
      this.isCounterpartyModalVisible = true;
    },

    async handleCounterpartyCreated(newCounterparty) {
      // Сначала обновляем стор чтобы новый контрагент попал в options селекта
      await this.fetchCounterparties();

      if (this.counterpartyModalTarget === "customer") {
        this.form.customer_id = newCounterparty.id;
        this.clearError("customer_id");
      } else if (this.counterpartyModalTarget === "contractor") {
        this.form.contractor_id = newCounterparty.id;
        this.clearError("contractor_id");
      }
      this.counterpartyModalTarget = null;
    },

    validateForm() {
      let isValid = true;

      this.errors = {
        customer_id: false,
        contractor_id: false,
      };

      if (!this.form.customer_id) {
        this.errors.customer_id = true;
        isValid = false;
      }

      if (!this.form.contractor_id) {
        this.errors.contractor_id = true;
        isValid = false;
      }

      return isValid;
    },

    async save() {
      const isValid = this.validateForm();

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
        return;
      }

      const payload = {
        ...this.form,
        amount: this.form.amount
          ? parseFloat(this.form.amount)
          : null,
      };

      try {
        if (this.isCreateMode) {
          await this.createInvoice(this.orderId, payload);
        } else {
          await this.updateInvoice(
            this.orderId,
            this.initialData.id,
            payload
          );
        }
        this.$emit("save");
      } catch (error) {
        console.error("Ошибка сохранения счёта:", error);
      }
    },
  },
};
</script>

<style scoped>
.invoice-form {
  padding: var(--base-in-indent);
  border-radius: 8px;
  background: #fff;
}

.create-mode {
  border: 2px dashed #d1d5db;
  background: #f9fafb;
}

.form-title {
  margin: 0 0 var(--base-in-indent);
  font-size: 16px;
  font-weight: 600;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: var(--base-in-indent);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.error-message {
  color: var(--color-danger);
  font-size: 12px;
}

.form-actions {
  display: flex;
  gap: 10px;
}

@media (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
